"use client";

import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { invokeEdgeFunction } from "@/lib/edgeFunctionClient";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Eye, Loader2, Send } from "lucide-react";
import { EmailPreviewModal } from "./EmailPreviewModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function CustomAnnouncementsTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Send announcement
  const sendMutation = useMutation({
    mutationFn: async () => {
      if (!subject.trim()) throw new Error("Please enter a subject line");
      if (!message.trim()) throw new Error("Please enter a message");
      
      const { data, error } = await invokeEdgeFunction('send-custom-announcement', {
        body: { subject, message },
        callerOperation: 'CustomAnnouncementsTab.send',
      });
      
      if (error) throw error;
      return data as { emails_queued?: number };
    },
    onSuccess: (data) => {
      toast({
        title: "Announcement sent",
        description: `Queued for ${data?.emails_queued || 0} vendors.`
      });
      setSubject("");
      setMessage("");
      queryClient.invalidateQueries({ queryKey: ['mass-communication-logs'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error sending announcement",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const handleSend = () => {
    if (!subject.trim() || !message.trim()) {
      toast({
        title: "Missing content",
        description: "Please enter both subject and message.",
        variant: "destructive"
      });
      return;
    }
    setShowConfirm(true);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Custom Announcement</CardTitle>
          <CardDescription>
            Send a custom announcement email to all verified vendors
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="subject">Subject Line</Label>
            <Input
              id="subject"
              placeholder="Important announcement from Radius"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={100}
            />
            <p className="text-xs text-muted-foreground">
              {subject.length}/100 characters
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              placeholder="Enter your announcement message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={8}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              This will be displayed in the email body. Plain text only.
            </p>
          </div>

          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              This will send an email to all verified vendors. Please review carefully before sending.
            </AlertDescription>
          </Alert>

          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => setShowPreview(true)}
              disabled={!subject && !message}
            >
              <Eye className="mr-2 h-4 w-4" />
              Preview Email
            </Button>
            <Button 
              onClick={handleSend}
              disabled={!subject.trim() || !message.trim() || sendMutation.isPending}
            >
              {sendMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Send to All Vendors
            </Button>
          </div>
        </CardContent>
      </Card>

      <EmailPreviewModal 
        open={showPreview} 
        onOpenChange={setShowPreview}
        templateType="custom_announcement"
        previewData={{ subject, message }}
      />

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Send</AlertDialogTitle>
            <AlertDialogDescription>
              This will send an email to all verified vendors. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4 space-y-2">
            <p className="text-sm font-medium">Subject:</p>
            <p className="text-sm text-muted-foreground">{subject}</p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => sendMutation.mutate()}
              disabled={sendMutation.isPending}
            >
              {sendMutation.isPending ? "Sending..." : "Send Now"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
